import React from 'react';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import { prefectures } from '../../../utils/prefectures';
import {
  AUTO_RANGE_LABELS,
  normalizeSelectionArray,
  normalizeSelectionValue,
} from './artistEventListUtils';
import DsSelect from '../../common/DsSelect';

function PeriodFilter({
  autoRange,
  filterEndDate,
  filterStartDate,
  handlePeriodReset,
  handlePeriodSearch,
  setAutoRangeAndSearch,
  setFilterEndDate,
  setFilterStartDate,
  variant = 'inline',
}) {
  return (
    <div className={`period-filter period-filter--${variant}`}>
      <DsSelect
        className="period-control"
        value={autoRange}
        onChange={(e) => setAutoRangeAndSearch(e.target.value)}
        aria-label="期間を選択"
      >
        {Object.entries(AUTO_RANGE_LABELS).map(([v, label]) => (
          <option key={v} value={v}>{label}</option>
        ))}
      </DsSelect>
      <div className="period-filter__dates">
        <input
          type="date"
          value={filterStartDate}
          onChange={(e) => setFilterStartDate(e.target.value)}
          className="period-control"
          aria-label="開始日"
        />
        <span>〜</span>
        <input
          type="date"
          value={filterEndDate}
          onChange={(e) => setFilterEndDate(e.target.value)}
          className="period-control"
          aria-label="終了日"
        />
        <DsButton
          type="button"
          variant="secondary"
          size="sm"
          icon
          className="period-reset-action"
          onClick={handlePeriodReset}
          aria-label="期間をリセット"
          title="期間をリセット"
        >
          <i className="fa-solid fa-arrow-rotate-left" aria-hidden="true"></i>
        </DsButton>
      </div>
      <div className="period-filter__actions">
        <DsButton type="button" className="period-action period-action--block" onClick={handlePeriodSearch}>
          期間検索
        </DsButton>
      </div>
    </div>
  );
}

function AdvancedFilters({
  allFetchedEvents,
  fetchAllVenues,
  filterEventName,
  filterGenres,
  filterLocally,
  filterPrefectures,
  filterRegions,
  filterVenues,
  generateFlyerUrls,
  setCurrentPage,
  setEvents,
  setFilterEventName,
  setFlyerUrls,
  setShowGenreFilterPopup,
  setShowPrefectureFilterPopup,
  setShowRegionFilterPopup,
  setShowVenueFilterPopup,
  variant = 'inline',
}) {
  return (
    <div className={`event-advanced-fields event-advanced-fields--${variant}`}>
      <div className="search-field">
        <input
          className="search-control"
          placeholder="キーワード"
          aria-label="キーワード"
          type="text"
          value={filterEventName}
          onChange={(e) => setFilterEventName(e.target.value)}
          onBlur={() => {
            const filtered = filterLocally(allFetchedEvents);
            setEvents(filtered);
            generateFlyerUrls(filtered).then((urls) => setFlyerUrls(urls));
            setCurrentPage(1);
          }}
        />
      </div>
      <div className="search-field">
        <DsButton
          type="button"
          variant="secondary"
          className="search-control select-filter-action"
          onClick={() => setShowGenreFilterPopup(true)}
        >
          {filterGenres.length ? `ジャンル選択（${filterGenres.length}件）` : 'ジャンル選択'}
        </DsButton>
      </div>
      <div className="search-field">
        <DsButton
          type="button"
          variant="secondary"
          className="search-control select-filter-action"
          onClick={() => setShowRegionFilterPopup(true)}
        >
          {filterRegions.length ? `エリア選択（${filterRegions.length}件）` : 'エリア選択'}
        </DsButton>
      </div>
      <div className="search-field">
        <DsButton
          type="button"
          variant="secondary"
          className="search-control select-filter-action"
          onClick={() => setShowPrefectureFilterPopup(true)}
        >
          {filterPrefectures.length ? `都道府県選択（${filterPrefectures.length}件）` : '都道府県選択'}
        </DsButton>
      </div>
      <div className="search-field venue-search-field">
        <DsButton
          type="button"
          variant="secondary"
          className="search-control select-filter-action"
          onClick={(e) => {
            e.stopPropagation();
            setShowVenueFilterPopup(true);
            fetchAllVenues();
          }}
        >
          会場を選択{filterVenues.length ? `（${filterVenues.length}件）` : ''}
        </DsButton>
      </div>
    </div>
  );
}

function ConditionChipBar({ chips, hasActiveConditions, onClear }) {
  if (!hasActiveConditions) return null;
  return (
    <div className="condition-chip-bar" aria-live="polite">
      <div className="condition-chip-list" role="list">
        {chips.map((chip) => (
          <span key={chip.key} className="condition-chip" role="listitem">
            <span className="condition-chip__label">{chip.label}</span>
            <button
              type="button"
              className="condition-chip__remove"
              aria-label={`${chip.label} を解除`}
              onClick={chip.onRemove}
            >
              ×
            </button>
          </span>
        ))}
        <button
          type="button"
          className="condition-chip condition-chip-clear"
          onClick={onClear}
        >
          すべてクリア
        </button>
      </div>
    </div>
  );
}

function FilterPickerModal({
  helperText,
  isOpen,
  onClear,
  onClose,
  onSearchChange,
  onToggle,
  options = [],
  searchPlaceholder,
  searchValue,
  selectedValues = [],
  title,
}) {
  if (!isOpen) return null;
  const normalizedSelected = normalizeSelectionArray(selectedValues);
  const keyword = typeof searchValue === 'string' ? searchValue.trim().toLowerCase() : '';
  const filteredOptions = Array.isArray(options)
    ? options.filter((opt) => {
        if (!keyword) return true;
        const label = (opt.label || '').toLowerCase();
        const value = normalizeSelectionValue(opt.value).toLowerCase();
        const meta = (opt.meta || '').toLowerCase();
        return label.includes(keyword) || value.includes(keyword) || meta.includes(keyword);
      })
    : [];

  return (
    <DsModal
      open={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      className="filter-multiselect-modal"
      footer={(
        <>
          <DsButton type="button" variant="secondary" size="sm" onClick={onClear}>
            すべて解除
          </DsButton>
          <DsButton type="button" size="sm" onClick={onClose}>
            完了
          </DsButton>
        </>
      )}
    >
      {helperText && <p className="filter-multiselect-helper">{helperText}</p>}
      {typeof searchValue === 'string' && onSearchChange && (
        <div className="filter-multiselect-search">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder || 'キーワードで絞り込み'}
          />
        </div>
      )}
      <div className="filter-multiselect-list" role="listbox" aria-multiselectable="true">
        {filteredOptions.map((opt) => {
          const optionValue = normalizeSelectionValue(opt.value);
          if (!optionValue) return null;
          const checked = normalizedSelected.includes(optionValue);
          return (
            <label
              key={optionValue}
              className={`filter-multiselect-option${checked ? ' is-selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(optionValue)}
              />
              <span className="filter-multiselect-option__label">{opt.label}</span>
              {opt.meta && <span className="filter-multiselect-option__meta">{opt.meta}</span>}
            </label>
          );
        })}
        {!filteredOptions.length && (
          <p className="filter-multiselect-empty">該当する項目がありません</p>
        )}
      </div>
    </DsModal>
  );
}

function VenueFilterModal({
  allVenues,
  filterVenues,
  setFilterVenues,
  setShowVenueFilterPopup,
  setVenueFilterPrefecture,
  setVenueFilterSearch,
  showVenueFilterPopup,
  toggleVenueSelection,
  venueFilterPrefecture,
  venueFilterSearch,
}) {
  return (
    <DsModal
      open={showVenueFilterPopup}
      onClose={() => setShowVenueFilterPopup(false)}
      title="会場を選択（複数可）"
      size="lg"
      className="venue-filter-modal"
      footer={(
        <>
          <DsButton type="button" variant="secondary" size="sm" onClick={() => setFilterVenues([])}>
            すべて解除
          </DsButton>
          <DsButton type="button" size="sm" onClick={() => setShowVenueFilterPopup(false)}>
            適用して閉じる
          </DsButton>
        </>
      )}
    >
      <div className="venue-filter-controls">
        <input type="text" placeholder="会場名検索" value={venueFilterSearch} onChange={(e) => setVenueFilterSearch(e.target.value)} />
        <DsSelect value={venueFilterPrefecture} onChange={(e) => setVenueFilterPrefecture(e.target.value)} className="prefecturs-select">
          <option value="">都道府県選択</option>
          {prefectures.map((pref) => (<option key={pref.value} value={pref.value}>{pref.label}</option>))}
        </DsSelect>
        <DsButton
          type="button"
          variant="secondary"
          size="sm"
          className="venue-filter-reset-action"
          onClick={() => { setVenueFilterSearch(''); setVenueFilterPrefecture(''); }}
        >
          リセット
        </DsButton>
      </div>
      <div className="venue-list">
        {allVenues
          .filter((venue) =>
            venue.name.toLowerCase().includes(venueFilterSearch.toLowerCase()) &&
            (venueFilterPrefecture ? venue.prefecture === venueFilterPrefecture : true)
          )
          .map((venue) => {
            const checked = normalizeSelectionArray(filterVenues).includes(String(venue.id));
            return (
              <label key={venue.id} className={`venue-list-item ${checked ? 'selected' : ''}`}>
                <input type="checkbox" checked={checked} onChange={() => toggleVenueSelection(venue)} />
                <span className="venue-name">{venue.name}</span>
                <span className="venue-pref">{venue.prefecture}</span>
              </label>
            );
          })}
      </div>
    </DsModal>
  );
}

export default function ArtistEventListFilters({
  advancedFilterProps,
  conditionChips,
  filterPickerProps,
  hasActiveConditions,
  isMobileSearchLayout,
  onResetFilters,
  periodFilterProps,
  setShowFilterPanel,
  showFilterPanel,
  showSearchPopup,
  setShowSearchPopup,
  venueFilterProps,
}) {
  const closeSearchPopup = () => setShowSearchPopup(false);
  return (
    <>
      {isMobileSearchLayout && (
        <div className="mobile-search-trigger list-filter-mobile-trigger">
          <DsButton
            type="button"
            variant="secondary"
            className={`mobile-search-trigger__button${hasActiveConditions ? ' is-active' : ''}`}
            onClick={() => setShowSearchPopup(true)}
            aria-haspopup="dialog"
            aria-expanded={showSearchPopup}
          >
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            <span>検索条件</span>
            {hasActiveConditions ? (
              <span className="mobile-search-trigger__badge">{conditionChips.length}</span>
            ) : null}
          </DsButton>
        </div>
      )}

      {!isMobileSearchLayout && (
        <div className="filter-panel-toggle-row list-filter-toggle-row">
          <DsButton
            type="button"
            variant="secondary"
            className={`filter-panel-toggle list-filter-toggle${showFilterPanel ? ' is-open' : ''}`}
            onClick={() => setShowFilterPanel((prev) => !prev)}
          >
            <i className={`fa-solid fa-chevron-${showFilterPanel ? 'up' : 'down'}`} aria-hidden="true"></i>
            <span>{showFilterPanel ? '検索条件を閉じる' : '検索条件を表示'}</span>
          </DsButton>
        </div>
      )}

      {!isMobileSearchLayout && showFilterPanel && (
        <div className="direct-search-controls">
          <div className="event-filter-fields">
            <PeriodFilter {...periodFilterProps} variant="inline" />
            <AdvancedFilters {...advancedFilterProps} variant="inline" />
          </div>
        </div>
      )}

      <ConditionChipBar
        chips={conditionChips}
        hasActiveConditions={hasActiveConditions}
        onClear={onResetFilters}
      />

      <DsModal
        open={showSearchPopup}
        onClose={closeSearchPopup}
        title="詳細絞り込み"
        size="sm"
        className="event-list-search-modal"
        footer={(
          <DsButton type="button" variant="secondary" onClick={closeSearchPopup}>
            閉じる
          </DsButton>
        )}
      >
        {isMobileSearchLayout && <PeriodFilter {...periodFilterProps} variant="modal" />}
        <AdvancedFilters {...advancedFilterProps} variant="modal" />
      </DsModal>

      <FilterPickerModal {...filterPickerProps.genre} />
      <FilterPickerModal {...filterPickerProps.region} />
      <FilterPickerModal {...filterPickerProps.prefecture} />
      <VenueFilterModal {...venueFilterProps} />
    </>
  );
}
